import { useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import {
  Card, PageHeader, Table, Btn, Badge, Modal,
  Input, SearchInput, FilterBar, FilterSelect,
} from '../components/UI';
import { IconPlus, IconEdit, IconTrash, IconCheck, IconX } from '../components/Icons';

// ── Lodge form (used in both Add and Edit modals) ─────────────────────────────
function LodgeForm({ initial = {}, onSubmit, onCancel, loading }) {
  const [name,      setName]      = useState(initial.name      ?? '');
  const [isActive,  setIsActive]  = useState(initial.is_active ?? true);
  const [error,     setError]     = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) { setError('Lodge name is required.'); return; }
    onSubmit({ name: name.trim(), is_active: isActive });
  }

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Input
        label="Lodge Name"
        value={name}
        onChange={v => { setName(v); setError(''); }}
        placeholder="e.g. Sunrise Lodge"
        required
        error={error}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          Status
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ val: true, label: 'Active' }, { val: false, label: 'Inactive' }].map(opt => (
            <button
              key={String(opt.val)}
              type="button"
              onClick={() => setIsActive(opt.val)}
              style={{
                flex: 1, padding: '9px 14px', borderRadius: 8, border: '1px solid',
                cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                transition: 'all 0.15s',
                borderColor: isActive === opt.val
                  ? (opt.val ? 'var(--green)' : 'var(--red)')
                  : 'var(--border2)',
                background: isActive === opt.val
                  ? (opt.val ? 'var(--green-dim)' : 'var(--red-dim)')
                  : 'var(--surface2)',
                color: isActive === opt.val
                  ? (opt.val ? 'var(--green)' : 'var(--red)')
                  : 'var(--text3)',
              }}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <Btn variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Btn>
        <Btn type="submit" icon={IconCheck} loading={loading}>
          {initial.id ? 'Save Changes' : 'Add Lodge'}
        </Btn>
      </div>
    </form>
  );
}

// ── Confirm delete modal ──────────────────────────────────────────────────────
function ConfirmDelete({ lodge, onConfirm, onCancel, loading }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ color: 'var(--text2)', fontSize: 14, margin: 0, lineHeight: 1.6 }}>
        Are you sure you want to delete <b style={{ color: 'var(--text)' }}>{lodge.name}</b>?
        {lodge.user_count > 0 && (
          <span style={{ display: 'block', marginTop: 8, color: 'var(--yellow)', fontSize: 13 }}>
            ⚠ This lodge has <b>{lodge.user_count}</b> user{lodge.user_count > 1 ? 's' : ''} assigned to it.
            Their lodge will be set to unassigned.
          </span>
        )}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Btn variant="ghost" onClick={onCancel} disabled={loading}>Cancel</Btn>
        <Btn variant="danger" icon={IconTrash} loading={loading} onClick={onConfirm}>
          Delete Lodge
        </Btn>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Lodges() {
  const [lodges,  setLodges]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [filter,  setFilter]  = useState('all'); // all | active | inactive

  const [modal, setModal] = useState(null); // null | { type: 'add' | 'edit' | 'delete', lodge? }
  const [saving, setSaving] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.getLodgesAdmin()
      .then(r => setLodges(r.lodges || []))
      .catch(e => toast(e.message, 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ── Filtered rows ────────────────────────────────────────────────────────────
  const rows = lodges.filter(l => {
    const matchSearch = !search || l.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'active' ? l.is_active : !l.is_active);
    return matchSearch && matchFilter;
  });

  // ── Stats ────────────────────────────────────────────────────────────────────
  const totalActive   = lodges.filter(l =>  l.is_active).length;
  const totalInactive = lodges.filter(l => !l.is_active).length;
  const totalUsers    = lodges.reduce((s, l) => s + (l.user_count || 0), 0);

  // ── CRUD handlers ────────────────────────────────────────────────────────────
  async function handleAdd(body) {
    setSaving(true);
    try {
      await api.createLodge(body);
      toast('Lodge added.', 'success');
      setModal(null);
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function handleEdit(body) {
    setSaving(true);
    try {
      await api.updateLodge(modal.lodge.id, body);
      toast('Lodge updated.', 'success');
      setModal(null);
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await api.deleteLodge(modal.lodge.id);
      toast('Lodge deleted.', 'success');
      setModal(null);
      load();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  }

  // ── Mobile card ──────────────────────────────────────────────────────────────
  const mobileCard = (l) => (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 3 }}>{l.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>{l.user_count || 0} user{l.user_count !== 1 ? 's' : ''}</div>
        </div>
        <Badge color={l.is_active ? 'green' : 'gray'}>{l.is_active ? 'Active' : 'Inactive'}</Badge>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <Btn size="xs" variant="ghost" icon={IconEdit} onClick={() => setModal({ type: 'edit', lodge: l })}>Edit</Btn>
        <Btn size="xs" variant="danger" icon={IconTrash} onClick={() => setModal({ type: 'delete', lodge: l })}>Delete</Btn>
      </div>
    </>
  );

  return (
    <div className="page-enter">
      <PageHeader
        title="Lodges"
        subtitle={`${lodges.length} lodge${lodges.length !== 1 ? 's' : ''} · ${totalUsers} users assigned`}
        action={
          <Btn icon={IconPlus} onClick={() => setModal({ type: 'add' })}>
            Add Lodge
          </Btn>
        }
      />

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Total Lodges',  value: lodges.length,  color: 'var(--accent)' },
          { label: 'Active',        value: totalActive,    color: 'var(--green)'  },
          { label: 'Inactive',      value: totalInactive,  color: 'var(--text2)'  },
          { label: 'Total Users',   value: totalUsers,     color: 'var(--purple)' },
        ].map(s => (
          <Card key={s.label} style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, fontFamily: 'Syne, sans-serif', color: s.color }}>{s.value}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search lodges…" />
        <FilterSelect
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all',      label: 'All Lodges' },
            { value: 'active',   label: 'Active'     },
            { value: 'inactive', label: 'Inactive'   },
          ]}
        />
        {(search || filter !== 'all') && (
          <Btn size="sm" variant="ghost" icon={IconX} onClick={() => { setSearch(''); setFilter('all'); }}>
            Clear
          </Btn>
        )}
      </FilterBar>

      {/* Table */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table
          loading={loading}
          rows={rows}
          mobileCard={mobileCard}
          onRowClick={l => navigate(`/lodges/${l.id}`)}
          emptyText={search ? `No lodges match "${search}".` : 'No lodges found.'}
          columns={[
            {
              key: 'name', label: 'Lodge Name',
              render: l => <span style={{ fontWeight: 600, color: 'var(--text)' }}>{l.name}</span>,
            },
            {
              key: 'user_count', label: 'Users',
              render: l => (
                <span style={{ color: 'var(--text2)', fontWeight: 600 }}>
                  {l.user_count || 0}
                </span>
              ),
            },
            {
              key: 'is_active', label: 'Status',
              render: l => <Badge color={l.is_active ? 'green' : 'gray'}>{l.is_active ? 'Active' : 'Inactive'}</Badge>,
            },
            {
              key: 'created_at', label: 'Created',
              render: l => (
                <span style={{ color: 'var(--text3)', fontSize: 12 }}>
                  {new Date(l.createdAt).toLocaleDateString('en-NG', { day: '2-digit', month: 'short', year: 'numeric' })}
                </span>
              ),
            },
            {
              key: 'actions', label: '',
              render: l => (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <Btn size="xs" variant="ghost" icon={IconEdit}
                    onClick={() => setModal({ type: 'edit', lodge: l })}>
                    Edit
                  </Btn>
                  <Btn size="xs" variant="danger" icon={IconTrash}
                    onClick={() => setModal({ type: 'delete', lodge: l })}>
                    Delete
                  </Btn>
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* ── Modals ── */}
      {modal?.type === 'add' && (
        <Modal title="Add Lodge" onClose={() => setModal(null)}>
          <LodgeForm
            onSubmit={handleAdd}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        </Modal>
      )}

      {modal?.type === 'edit' && (
        <Modal title={`Edit — ${modal.lodge.name}`} onClose={() => setModal(null)}>
          <LodgeForm
            initial={modal.lodge}
            onSubmit={handleEdit}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        </Modal>
      )}

      {modal?.type === 'delete' && (
        <Modal title="Delete Lodge" onClose={() => setModal(null)} width={440}>
          <ConfirmDelete
            lodge={modal.lodge}
            onConfirm={handleDelete}
            onCancel={() => setModal(null)}
            loading={saving}
          />
        </Modal>
      )}
    </div>
  );
}